<template>
  <PageHeader>
    <template #header-title>
      {{ $t('Clients') }}
    </template>
    <template #header-description>
      <WdsStack justify-space-between>
        <WdsText>
          {{ $t('List of companies that have added you to their talent pool.') }}
        </WdsText>
      </WdsStack>
    </template>
    <template #header-actions>
      <WdsButton
        tiertary
        :href="laravelNamedRoute(namedRoutes.frontend.user.profile.edit)"
        class="d-flex align-items-center tw-gap-1"
      >
        {{ $t('Account settings') }}
        <WdsIcon name="arrow-up-right" :size="14" />
      </WdsButton>
    </template>
  </PageHeader>

  <div v-if="loading" class="tw-flex tw-items-center tw-justify-center tw-py-12">
    <WdsSpinner indeterminate />
  </div>

  <WdsStack v-else-if="hasClients" direction-column gap="4">
    <ClientCard
      v-for="client in filteredClients"
      :key="client.id"
      :client="{ id: client.id, status: client.status, company: client.company }"
    >
      <template #subtitle>
        <WdsText secondary class="tw-ml-3">
          {{
            client.status === ContactStatus.Active
              ? $t('Trusted Contact since :date', { date: client.invitedAt })
              : $t('Invited on :date', { date: client.invitedAt })
          }}
        </WdsText>
      </template>

      <template #actions>
        <template v-if="client.status === ContactStatus.Invited">
          <WdsButton @click="declineInvitation(client)">
            {{ $t('Decline') }}
          </WdsButton>
          <WdsButton
            secondary
            :href="laravelNamedRoute('contacts.join', { id: client.id })"
          >
            {{ $t('Accept invitation') }}
          </WdsButton>
        </template>
        <template v-else-if="client.status === ContactStatus.Active">
          <WdsLabel
            v-if="hasRequirements(client)"
            subtle
            :success="requirementsComplete(client)"
            :warning="!requirementsComplete(client)"
          >
            {{ requirementsComplete(client) ? $t('Requirements complete') : $t('Requirements missing') }}
          </WdsLabel>
          <WdsButton
            v-if="hasRequirements(client)"
            secondary
            class="tw-items-center"
            @click="openProfileModal(client)"
          >
            {{ $t('Edit profile') }}
            <WdsIcon name="chevron-right" :size="16" class="tw-ml-1" />
          </WdsButton>
        </template>
      </template>

      <template #banner>
        <WdsStack
          v-if="client.status === ContactStatus.Invited && client.requiresOnboarding"
          justify-space-between
          align-center
          class="tw-bg-orange-50 tw-border tw-border-orange-200 tw-border-l-0 tw-border-r-0 tw-py-2 tw-px-4 tw-w-full"
          gap-sm
        >
          <WdsStack align-center gap-sm>
            <WdsIcon :name="WdsIconNameEnum.WARNING" :size="20" class="tw-text-orange-500" />
            <WdsText>
              <span class="tw-text-orange-500">
                {{
                  $t(
                    'Complete your :company profile to access their jobs and offers.',
                    { company: client.company.name },
                  )
                }}
              </span>
            </WdsText>
          </WdsStack>
          <WdsButton primary sm class="tw-items-center" @click="openProfileModal(client)">
            {{ $t('Complete') }}
            <WdsIcon name="chevron-right" :size="16" class="tw-ml-1" />
          </WdsButton>
        </WdsStack>
        <WdsStack
          v-else-if="client.status === ContactStatus.Active && hasRequirements(client) && !requirementsComplete(client)"
          justify-space-between
          align-center
          class="tw-bg-orange-50 tw-border tw-border-orange-200 tw-border-l-0 tw-border-r-0 tw-py-2 tw-px-4 tw-w-full"
          gap-sm
        >
          <WdsStack align-center gap-sm>
            <WdsIcon :name="WdsIconNameEnum.WARNING" :size="20" class="tw-text-orange-500" />
            <WdsText>
              <span class="tw-text-orange-500">
                {{
                  $t(
                    "Complete your profile for this client's talent pool to access jobs and offers from them.",
                  )
                }}
              </span>
            </WdsText>
          </WdsStack>
          <WdsButton primary sm class="tw-items-center" @click="openProfileModal(client)">
            {{ $t('Complete') }}
            <WdsIcon name="chevron-right" :size="16" class="tw-ml-1" />
          </WdsButton>
        </WdsStack>
      </template>
    </ClientCard>
  </WdsStack>

  <WdsStack v-else>
    <WdsStack
      direction-column
      gap-sm
      align-center
      justify-center
      class="tw-bg-true-gray-50 tw-rounded-md tw-p-6 tw-w-full"
    >
      <WdsHeading type="title2">
        {{ $t('You have no clients') }}
      </WdsHeading>
      <WdsText>
        {{ $t('To get started, you need to be invited by a company.') }}
      </WdsText>
    </WdsStack>
  </WdsStack>

  <WorkerProfileModal
    v-if="selectedClient"
    :show="showProfileModal"
    :client-id="selectedClient.id"
    :company-name="selectedClient.company.name"
    :company-id="selectedClient.company.id"
    :documents="selectedClientDocuments"
    :documents-loading="documentsRefetching"
    :custom-field-values="selectedClient.customFieldValues"
    @close="closeProfileModal"
    @saved="handleSaved"
    @document-accepted="handleDocumentAccepted"
  />
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import {
  WdsButton,
  WdsHeading,
  WdsIcon,
  WdsIconNameEnum,
  WdsLabel,
  WdsSpinner,
  WdsStack,
  WdsText,
} from '@worksome/wds-components'
import { useQuery } from '@vue/apollo-composable'
import PageHeader from './PageHeader.vue'
import ClientCard from './Clients/ClientCard.vue'
import WorkerProfileModal from './WorkerClients/WorkerProfileModal.vue'
import { graphql } from '@/gql'
import { useTranslation } from '@/composables/translation'
import { laravelNamedRoute } from '@/utils/laravelNamedRoutes'
import namedRoutes from '@/utils/laravelNamedRoutes.json'
import { ContactStatus, TrustedContactOnboardingStatus } from '@/gql/graphql'

const { $t } = useTranslation()

const workerId = currentAccount.globalId

const { result, loading, refetch } = useQuery(
  graphql(`
    query WorkerClientsPage($id: ID!) {
      worker(id: $id) {
        id
        clients {
          id
          status
          invitedAt
          token
          origin
          originChannel
          requiresOnboarding
          canLeaveClient
          onboardingStatus
          requiredCustomFieldsComplete
          company {
            id
            name
            avatar
            profile {
              id
              url
            }
          }
          onboardingDocuments {
            id
            pdfUrl
            isAccepted
            freelancerAcceptedAt
            file {
              id
              name
              title
            }
          }
          customFieldValues {
            ...FieldableFormCustomFieldValue
          }
        }
      }
    }
  `),
  { id: workerId },
  { clientId: 'publicApi' },
)

type ClientFromQuery = NonNullable<
  NonNullable<typeof result.value>['worker']
>['clients'][number]

const clients = computed(() => result.value?.worker?.clients ?? [])

const filteredClients = computed(() =>
  clients.value.filter(
    (client) =>
      client.status === ContactStatus.Active ||
      client.status === ContactStatus.Invited,
  ),
)

const hasClients = computed(() => filteredClients.value.length > 0)

const hasRequirements = (client: ClientFromQuery): boolean =>
  client.requiresOnboarding && (
    client.onboardingDocuments.length > 0 ||
    client.customFieldValues.length > 0 ||
    !client.requiredCustomFieldsComplete
  )

const requirementsComplete = (client: ClientFromQuery): boolean => {
  const docsComplete = client.onboardingDocuments.every((doc) => doc.isAccepted)
  const onboardingOk =
    client.onboardingStatus === null ||
    client.onboardingStatus === TrustedContactOnboardingStatus.Completed
  return docsComplete && onboardingOk && client.requiredCustomFieldsComplete
}

const showProfileModal = ref(false)
const selectedClientId = ref<string | null>(null)

const selectedClient = computed(() =>
  filteredClients.value.find((c) => c.id === selectedClientId.value) ?? null,
)

const selectedClientDocuments = computed(() => {
  if (!selectedClient.value) return []
  return selectedClient.value.onboardingDocuments.map((doc) => ({
    id: doc.id,
    pdfUrl: doc.pdfUrl ?? null,
    isAccepted: doc.isAccepted,
    file: doc.file
      ? { id: doc.file.id, name: doc.file.name, title: doc.file.title ?? null }
      : null,
  }))
})

const openProfileModal = (client: ClientFromQuery) => {
  selectedClientId.value = client.id
  showProfileModal.value = true
}

const autoOpenCompanyId = new URLSearchParams(window.location.search).get('company')

watch(filteredClients, (loadedClients) => {
  if (!autoOpenCompanyId || loadedClients.length === 0) return

  const client = loadedClients.find((c) => c.company.id === autoOpenCompanyId)
  if (client) {
    openProfileModal(client)
    const url = new URL(window.location.href)
    url.searchParams.delete('company')
    window.history.replaceState({}, '', url.toString())
  }
}, { once: true })

const closeProfileModal = () => {
  showProfileModal.value = false
  selectedClientId.value = null
}

const handleSaved = async () => {
  await refetch()
  closeProfileModal()
}

const documentsRefetching = ref(false)

const handleDocumentAccepted = async () => {
  documentsRefetching.value = true
  await refetch()
  documentsRefetching.value = false
}

const declineInvitation = (client: ClientFromQuery) => {
  if (client.token) {
    window.location.href = laravelNamedRoute('contacts.ask-decline', {
      id: client.id,
      token: client.token,
    })
  }
}
</script>
